// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Authentication Service
 *
 * Manages authentication state via MSAL.js (Azure Entra External ID / CIAM).
 * Token acquisition, refresh, and caching are handled by MSAL.
 * User profile data is hydrated from the /api/v1/me endpoint and stored in
 * localStorage for fast synchronous reads by UI components.
 */

import { msalInstance, loginRequest, getTokenAsync } from '../config/authConfig';
import { getUserData, removeUserData } from './tokenStorage';
import type { UserData } from '../types';
import type { MeResponse } from '../types';
import type { UserRole } from '../types';

export { getTokenAsync };

// ---------------------------------------------------------------------------
// Login / Logout
// ---------------------------------------------------------------------------

/**
 * Trigger a CIAM login redirect.
 * Call this from the Login page or when the user needs to re-authenticate.
 */
export const login = async (): Promise<void> => {
  await msalInstance.loginRedirect(loginRequest);
};

/**
 * Log out the current user.
 * Clears local user data (removeUserData, which also drops the alarm↔work-order
 * links) and redirects to the CIAM logout endpoint.
 */
export const logout = async (): Promise<void> => {
  removeUserData();
  await msalInstance.logoutRedirect({
    postLogoutRedirectUri: `${window.location.origin}/login`,
  });
};

// ---------------------------------------------------------------------------
// Authentication state
// ---------------------------------------------------------------------------

/**
 * Check if a user is authenticated (MSAL has an active account).
 * Synchronous — checks the in-memory MSAL cache, not the network.
 */
export const isAuthenticated = (): boolean => {
  return msalInstance.getActiveAccount() !== null;
};

// ---------------------------------------------------------------------------
// User data (hydrated from /api/v1/me, stored in localStorage)
// ---------------------------------------------------------------------------

/**
 * Get stored user data (sourced from /me response, not token decode)
 */
export const getUser = (): UserData | null => {
  return getUserData();
};

/** Normalize an IAM role-name display string: lowercase, with runs of spaces,
 *  hyphens, and underscores collapsed to a single underscore. IAM sends role
 *  names under any of these separators. */
const normalizeRoleName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');

/**
 * Normalized IAM role name -> canonical UserRole.
 *
 * The authoritative names come from `permissions-manifest.yaml` in
 * helios-gitops (`apps/iam-service/base/`). This is the ONE table to edit when
 * IAM adds or renames a role.
 *
 * `HeliosIamAdmin` is deliberately absent: it is a platform/bootstrap service
 * principal role (TenantId null, above all organizations) with no UI meaning.
 * Leaving it unmapped makes it resolve to `null` rather than to an admin.
 *
 * `admin` maps to itself so the legacy mock name "Admin" keeps working.
 */
const ROLE_ALIASES: Readonly<Record<string, UserRole>> = {
  orgadmin: 'admin',
  admin: 'admin',
  operations_manager: 'operations_manager',
  operator: 'operator',
  executive: 'executive',
};

/** Map one IAM role name (e.g. "OrgAdmin") to its canonical UserRole, or null if unrecognized. */
export const toUserRole = (name: string): UserRole | null =>
  ROLE_ALIASES[normalizeRoleName(name)] ?? null;

// executive > operations_manager is carried over from the BFF table this
// replaced; IAM issues one role per user today, so it has yet to decide anything.
const ROLE_RANK: Readonly<Record<UserRole, number>> = {
  operator: 0,
  operations_manager: 1,
  executive: 2,
  admin: 3,
};

/**
 * Resolve the view-driving role from a /me response.
 *
 * `/api/v1/me` relays IAM's `roles` array verbatim and no longer sends a
 * singular `role` — this is the only place the array is collapsed to one role.
 * Selection is by rank, not array position: IAM does not order `roles`, so the
 * old `roles[0]` pick could return `operator` for a user who was also an
 * OrgAdmin. Unrecognized names are ignored; an all-unrecognized list yields
 * `null`.
 */
export const deriveEffectiveRole = (me: Pick<MeResponse, 'roles'>): UserRole | null => {
  let best: UserRole | null = null;
  for (const name of me.roles ?? []) {
    const mapped = toUserRole(name);
    if (mapped && (best === null || ROLE_RANK[mapped] > ROLE_RANK[best])) {
      best = mapped;
    }
  }
  return best;
};

/**
 * Project a /api/v1/me response onto the flat UserData shape stored in
 * localStorage for fast synchronous reads by UI components. Pure — the caller
 * fetches /me once (see AuthContext) and persists the result via setUserData.
 */
export const meToUserData = (data: MeResponse): UserData => ({
  user_id: data.id,
  email: data.email,
  first_name: data.first_name,
  last_name: data.last_name,
  org_id: data.organization_id,
  org_name: data.organization?.preferredName ?? null,
  default_role: deriveEffectiveRole(data),
  user_group_ids: [],
  effective_permissions: data.permissions ?? [],
});

// ---------------------------------------------------------------------------
// Role / permission helpers (read from stored user data)
// ---------------------------------------------------------------------------

/**
 * Get user's role
 */
export const getUserRole = (): UserRole | null => {
  const user = getUser();
  return user?.default_role || null;
};

/**
 * Check if user has a specific permission string (e.g. "iam.user.create")
 */
export const hasPermission = (permission: string): boolean => {
  const user = getUser();
  if (!user?.effective_permissions) return false;
  return user.effective_permissions.includes(permission);
};

/**
 * Check if user has specific role or higher.
 * Role hierarchy: operator < operations_manager < admin.
 * Executive is orthogonal (read-only across all scopes).
 */
export const hasRole = (role: UserRole): boolean => {
  const userRole = getUserRole();
  const hierarchy: UserRole[] = ['operator', 'operations_manager', 'admin'];

  if (!userRole) return false;

  if (role === 'executive' || userRole === 'executive') {
    return userRole === role;
  }

  const userLevel = hierarchy.indexOf(userRole);
  const requiredLevel = hierarchy.indexOf(role);

  if (userLevel === -1 || requiredLevel === -1) return false;
  return userLevel >= requiredLevel;
};

/**
 * Check if user is an admin
 */
export const isAdmin = (): boolean => {
  const user = getUser();
  return user?.default_role === 'admin';
};
